import type { CSSProperties, HTMLAttributes } from 'react';
import React from 'react';
import { isDiagonal, tileFootprint, tileOrigin } from './board-layout';
import styles from './board-tile.module.scss';
import type { TileData } from './board-types';

export interface BoardTileFaceProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  tile: TileData;
  cellSize: number;
  gap: number;
  /**
   * Board padding. When supplied the face is absolutely positioned at the tile's board coordinates;
   * omit it to render the face in normal flow, as the drag overlay does.
   */
  padding?: number;
  /** Lifted drag treatment — scale + deeper shadow, per the design handoff. */
  lifted?: boolean;
  /**
   * Letter and badge only, no chrome — for a tile inside a cluster, where the cluster's shape draws
   * the surface and border for the whole group.
   */
  bare?: boolean;
  /** Draws the points badge. Off for a game whose letters score nothing. */
  showPoints?: boolean;
}

/**
 * The visual presentation of a board tile: chrome, per-character cells and points badge, sized from
 * `cellSize`. Deliberately free of interaction concerns and — unless `padding` is given — of board
 * positioning, so the drag overlay can reuse it outside the board's positioning context.
 */
export const BoardTileFace = React.forwardRef<HTMLDivElement, BoardTileFaceProps>(function BoardTileFace(
  { tile, cellSize, gap, padding, lifted = false, bare = false, showPoints = true, className, style, ...rest },
  ref,
) {
  const { width, height } = tileFootprint(tile, cellSize, gap);
  const origin = padding != null ? tileOrigin(tile, cellSize, gap, padding) : null;
  const innerInset = Math.max(2, cellSize * 0.08);
  const letterCount = Math.max(tile.letter.length, 1);
  /** A run stepping corner to corner: its letters take a diagonal of the square box, not a whole row. */
  const staircase = isDiagonal(tile.dir) && letterCount > 1;

  /**
   * The badge sits in the top-right corner of the tile's *topmost* cell, which for every other
   * direction is the top-right of the box itself. A `dr` run climbs away from that corner — its top
   * cell is the leftmost one — so the badge is pushed back across the empty cells beside it.
   */
  const badgeInset = tile.dir === 'dr' && letterCount > 1 ? (letterCount - 1) * (cellSize + gap) : 0;

  const faceClassName = [
    styles.tile,
    origin ? styles.positioned : null,
    tile.ghost ? styles.ghost : null,
    lifted ? styles.lifted : null,
    bare ? styles.bare : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={faceClassName}
      style={
        {
          ...(origin && { '--tile-left': `${origin.left}px`, '--tile-top': `${origin.top}px` }),
          '--tile-width': `${width}px`,
          '--tile-height': `${height}px`,
          '--tile-radius': `${Math.max(5, cellSize * 0.2)}px`,
          '--inner-inset': `${innerInset}px`,
          '--inner-radius': `${Math.max(3, cellSize * 0.14)}px`,
          '--letter-font-size': `${Math.min(cellSize, Math.max(11, Math.round(cellSize * 0.48)))}px`,
          '--letters-template': `repeat(${letterCount}, ${cellSize}px)`,
          '--cell-gap': `${gap}px`,
          '--badge-font-size': `${Math.max(5, Math.round(cellSize * 0.24))}px`,
          '--badge-right': `${1 + innerInset + badgeInset}px`,
          '--badge-top': `${Math.max(1, Math.round(cellSize * 0.04)) + innerInset}px`,
          ...style,
        } as CSSProperties
      }
      {...rest}
    >
      {/* A ghost is an outline only — no fill, no letters — so any bonus underneath shows through. */}
      {!tile.ghost && (
        <>
          <div className={styles.inner} />
          {/*
           * Spans the full, uninset tile so each letter centres on its true cell — matching a
           * standalone tile — rather than the whole run's characters dividing `inner`'s inset box
           * evenly between them, which drifts every character but the first off its cell centre.
           *
           * A single-character tile has no row or column to lay out, but the two grid templates
           * still round to different sub-pixel centres for the same 1-cell box. Fixing it to
           * `lettersRow` regardless of `dir` keeps that one glyph pixel-identical however the tile
           * is oriented — load-bearing when two same-cell tiles (e.g. a collision) must overlap
           * exactly rather than swim by a stray pixel.
           *
           * A diagonal run gets both templates and places each letter explicitly, so the glyphs step
           * down (`dr`) or up (`ur`) the square box while the cells between them stay empty.
           */}
          <div
            className={`${styles.letters} ${
              staircase
                ? styles.lettersDiagonal
                : tile.dir === 'v' && letterCount > 1
                  ? styles.lettersColumn
                  : styles.lettersRow
            }`}
          >
            {tile.letter.split('').map((ch, i) => (
              <div
                key={i}
                className={styles.letter}
                style={
                  staircase ? { gridColumn: i + 1, gridRow: tile.dir === 'dr' ? i + 1 : letterCount - i } : undefined
                }
              >
                <span className={styles.glyph}>{ch}</span>
              </div>
            ))}
          </div>
          {showPoints && (
            <span
              aria-hidden="true"
              className={tile.reducedPoints ? `${styles.badge} ${styles.reducedBadge}` : styles.badge}
            >
              {tile.points}
            </span>
          )}
        </>
      )}
    </div>
  );
});
